import { useCallback, useMemo } from "react";
import { useLocation, useSearch } from "wouter";

export function useQueryParams() {
  const searchString = useSearch();
  const [location, setLocation] = useLocation();

  const params = useMemo(() => new URLSearchParams(searchString), [searchString]);

  const getParam = useCallback(
    (key: string): string | null => {
      return params.get(key);
    },
    [params],
  );

  const buildUrl = useCallback(
    (newParams: URLSearchParams) => {
      const qs = newParams.toString();
      const basePath = location.split("?")[0];
      return qs ? `${basePath}?${qs}` : basePath;
    },
    [location],
  );

  const setParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(params);
      next.set(key, value);
      setLocation(buildUrl(next), { replace: true });
    },
    [params, setLocation, buildUrl],
  );

  const removeParam = useCallback(
    (key: string) => {
      const next = new URLSearchParams(params);
      next.delete(key);
      setLocation(buildUrl(next), { replace: true });
    },
    [params, setLocation, buildUrl],
  );

  const setParams = useCallback(
    (obj: Record<string, string | null>) => {
      const next = new URLSearchParams(params);
      for (const [key, value] of Object.entries(obj)) {
        if (value === null) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      }
      setLocation(buildUrl(next), { replace: true });
    },
    [params, setLocation, buildUrl],
  );

  return { getParam, setParam, removeParam, setParams, params };
}
